import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  FireIcon,
  HeartIcon,
  BoltIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { dailyActivityAPI, calorieAdherenceAPI } from '../../services/api'

// --- VH-54: Editable fields for activity logs ---
const ACTIVITY_FIELDS = [
  { key: 'steps', type: 'number', labelKey: 'activity.steps' },
  { key: 'calories_burned', type: 'number', labelKey: 'activity.caloriesBurned' },
  { key: 'resting_heart_rate', type: 'number', labelKey: 'activity.restingHR' },
  { key: 'max_heart_rate', type: 'number', labelKey: 'activity.maxHR' },
  { key: 'intensity_minutes', type: 'number', labelKey: 'activity.intensityMinutes' },
  { key: 'body_battery_gain', type: 'number', labelKey: 'activity.bodyBatteryGain' },
  { key: 'body_battery_drain', type: 'number', labelKey: 'activity.bodyBatteryDrain' },
  { key: 'training_duration_minutes', type: 'number', labelKey: 'activity.trainingDuration' },
  { key: 'training_calories', type: 'number', labelKey: 'activity.trainingCalories' },
]

// --- VH-54: Activity Entry Form (inline edit or create) ---
function ActivityEntryForm({ initialData, onSave, onCancel, isPending, t }) {
  const [form, setForm] = useState(() => {
    if (initialData) {
      return {
        log_date: initialData.log_date || '',
        ...ACTIVITY_FIELDS.reduce((acc, f) => {
          acc[f.key] = initialData[f.key] ?? ''
          return acc
        }, {}),
      }
    }
    return {
      log_date: new Date().toISOString().split('T')[0],
      ...ACTIVITY_FIELDS.reduce((acc, f) => { acc[f.key] = ''; return acc }, {}),
    }
  })

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = { log_date: form.log_date }
    ACTIVITY_FIELDS.forEach((f) => {
      const val = form[f.key]
      if (val !== '' && val !== null && val !== undefined) {
        payload[f.key] = Number(val)
      }
    })
    onSave(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('common.date', 'Date')}
        </label>
        <input
          type="date"
          value={form.log_date}
          onChange={(e) => updateField('log_date', e.target.value)}
          className="input text-sm w-full max-w-xs"
          required
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {ACTIVITY_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs text-gray-500 mb-1">{t(f.labelKey, f.key)}</label>
            <input
              type="number"
              value={form[f.key]}
              onChange={(e) => updateField(f.key, e.target.value)}
              className="input text-sm w-full"
              placeholder="—"
              min="0"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={isPending} className="btn-primary flex items-center text-sm">
          <CheckIcon className="h-4 w-4 mr-1" />
          {isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary text-sm">
          <XMarkIcon className="h-4 w-4 mr-1 inline" />
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </form>
  )
}

// --- VH-54: Delete Confirmation Dialog ---
function DeleteConfirmDialog({ onConfirm, onCancel, isPending, t }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
      <span className="text-sm text-red-700">{t('common.confirmDelete', 'Are you sure you want to delete this entry?')}</span>
      <div className="flex gap-2 ml-3">
        <button onClick={onConfirm} disabled={isPending} className="px-3 py-1 bg-red-600 text-on-neutral text-sm rounded hover:bg-red-700">
          {t('common.delete', 'Delete')}
        </button>
        <button onClick={onCancel} className="px-3 py-1 bg-surface-container text-gray-600 text-sm rounded border hover:bg-gray-50">
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  )
}

// --- DailyActivityCard ---

function DailyActivityCard() {
  const { t } = useTranslation()

  const { data, isLoading } = useQuery({
    queryKey: ['dailyActivityLatest'],
    queryFn: () => dailyActivityAPI.getLatest(),
    select: (res) => res.data,
    refetchOnMount: 'always',
  })

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body animate-pulse">
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  const log = data?.log
  const daysAgo = data?.days_ago

  if (!log) {
    return (
      <div className="card">
        <div className="card-body text-center py-8">
          <BoltIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{t('activity.noData')}</p>
          <p className="text-sm text-gray-400 mt-1">{t('activity.noDataHint')}</p>
        </div>
      </div>
    )
  }

  const metrics = [
    { key: 'caloriesBurned', value: log.calories_burned, unit: t('activity.kcal'), icon: FireIcon, color: 'text-orange-600', bg: 'bg-orange-50' },
    { key: 'steps', value: log.steps, unit: '', icon: BoltIcon, color: 'text-blue-600', bg: 'bg-blue-50' },
    { key: 'restingHR', value: log.resting_heart_rate, unit: t('activity.bpm'), icon: HeartIcon, color: 'text-red-600', bg: 'bg-red-50' },
    { key: 'maxHR', value: log.max_heart_rate, unit: t('activity.bpm'), icon: HeartIcon, color: 'text-pink-600', bg: 'bg-pink-50' },
    { key: 'intensityMinutes', value: log.intensity_minutes, unit: t('activity.min'), icon: ClockIcon, color: 'text-green-600', bg: 'bg-green-50' },
  ].filter((m) => m.value !== null && m.value !== undefined)

  const bodyBattery = (log.body_battery_gain != null || log.body_battery_drain != null)
    ? { gain: log.body_battery_gain, drain: log.body_battery_drain }
    : null

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium text-gray-900">
            {daysAgo === 0
              ? t('activity.todayActivity')
              : t('activity.latestActivity')
            }
          </h4>
          <div className="text-sm text-gray-500">
            {new Date(log.log_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
            {daysAgo > 2 && (
              <span className="ml-2 text-amber-600">
                <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                {daysAgo} {t('activity.daysAgo')}
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {metrics.map((m) => {
            const Icon = m.icon
            return (
              <div key={m.key} className={`${m.bg} rounded-lg p-3`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`h-4 w-4 ${m.color}`} />
                  <span className="text-xs text-gray-500">{t(`activity.${m.key}`)}</span>
                </div>
                <p className={`text-lg font-semibold ${m.color}`}>
                  {typeof m.value === 'number' && m.value >= 1000
                    ? m.value.toLocaleString()
                    : m.value
                  } <span className="text-xs font-normal">{m.unit}</span>
                </p>
              </div>
            )
          })}
          {bodyBattery && (
            <div className="bg-emerald-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <BoltIcon className="h-4 w-4 text-emerald-600" />
                <span className="text-xs text-gray-500">{t('activity.bodyBattery')}</span>
              </div>
              <p className="text-lg font-semibold text-emerald-600">
                {bodyBattery.gain != null && <span>+{bodyBattery.gain}</span>}
                {bodyBattery.gain != null && bodyBattery.drain != null && ' / '}
                {bodyBattery.drain != null && <span>-{bodyBattery.drain}</span>}
              </p>
            </div>
          )}
        </div>
        {(log.training_duration_minutes || log.training_calories) && (
          <div className="mt-3 p-3 bg-amber-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">{t('activity.training')}</p>
            <div className="flex gap-4 text-sm font-medium text-amber-700">
              {log.training_duration_minutes && <span>{log.training_duration_minutes} {t('activity.min')}</span>}
              {log.training_calories && <span>{log.training_calories} {t('activity.kcal')}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// --- CalorieBalanceBar ---

function CalorieBalanceBar() {
  const { t } = useTranslation()

  const { data, isLoading } = useQuery({
    queryKey: ['calorieAdherence', 'today'],
    queryFn: () => {
      const today = new Date().toISOString().split('T')[0]
      return calorieAdherenceAPI.getAdherence({ start_date: today, end_date: today })
    },
    select: (res) => res.data?.days?.[0],
  })

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body animate-pulse">
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  if (!data) return null

  const { calories_consumed, calories_burned, net_calories, target_calories, status, warnings } = data

  const hasNutrition = calories_consumed != null
  const hasGarmin = calories_burned != null

  if (!hasNutrition && !hasGarmin) return null

  const maxVal = Math.max(calories_consumed || 0, calories_burned || 0, target_calories || 0, 1)

  const statusColors = {
    on_target: { bg: 'bg-green-100', text: 'text-green-700', label: t('activity.onTarget') },
    surplus: { bg: 'bg-red-100', text: 'text-red-700', label: t('activity.surplus') },
    deficit: { bg: 'bg-amber-100', text: 'text-amber-700', label: t('activity.deficit') },
  }
  const statusInfo = status ? statusColors[status] : null

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium text-gray-900">{t('activity.calorieBalance')}</h4>
          {statusInfo && (
            <span className={`text-xs px-2 py-1 rounded-full ${statusInfo.bg} ${statusInfo.text} font-medium`}>
              {statusInfo.label}
            </span>
          )}
        </div>

        {/* Bars */}
        <div className="space-y-2">
          {hasNutrition && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{t('activity.consumed')}</span>
                <span className="font-medium text-gray-700">{Math.round(calories_consumed)} {t('activity.kcal')}</span>
              </div>
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all"
                  style={{ width: `${Math.min((calories_consumed / maxVal) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
          {hasGarmin && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{t('activity.burned')}</span>
                <span className="font-medium text-gray-700">{calories_burned} {t('activity.kcal')}</span>
              </div>
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 rounded-full transition-all"
                  style={{ width: `${Math.min((calories_burned / maxVal) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
          {target_calories && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{t('activity.target')}</span>
                <span className="font-medium text-gray-700">{target_calories} {t('activity.kcal')}</span>
              </div>
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-400 rounded-full transition-all"
                  style={{ width: `${Math.min((target_calories / maxVal) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Net balance */}
        {net_calories != null && (
          <div className="mt-3 pt-3 border-t flex justify-between items-center">
            <span className="text-sm text-gray-500">{t('activity.netBalance')}</span>
            <span className={`text-lg font-bold ${net_calories > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {net_calories > 0 ? '+' : ''}{Math.round(net_calories)} {t('activity.kcal')}
            </span>
          </div>
        )}

        {/* Warnings */}
        {warnings?.length > 0 && (
          <div className="mt-2 space-y-1">
            {warnings.includes('missing_nutrition_data') && (
              <p className="text-xs text-gray-400">{t('activity.noNutritionData')}</p>
            )}
            {warnings.includes('no_active_goal') && (
              <p className="text-xs text-gray-400">{t('activity.noGoalSet')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// --- MultiWeekActivityHistory (VH-54: with edit/delete per daily log) ---

function MultiWeekActivityHistory() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [expandedWeek, setExpandedWeek] = useState(null)
  const [weeksToLoad, setWeeksToLoad] = useState(4)
  const [editingLogId, setEditingLogId] = useState(null)
  const [deletingLogId, setDeletingLogId] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)

  const { data: multiWeekData, isLoading } = useQuery({
    queryKey: ['activityMultiWeek', weeksToLoad],
    queryFn: () => dailyActivityAPI.getMultiWeekSummary({ weeks: weeksToLoad }),
    select: (res) => res.data,
    refetchOnMount: 'always',
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['activityMultiWeek'] })
    queryClient.invalidateQueries({ queryKey: ['dailyActivityLatest'] })
  }

  const updateMutation = useMutation({
    mutationFn: ({ logId, data }) => dailyActivityAPI.updateLog(logId, data),
    onSuccess: () => {
      invalidateAll()
      setEditingLogId(null)
      toast.success(t('activity.updateSuccess', 'Activity entry updated'))
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('common.error', 'Error'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (logId) => dailyActivityAPI.deleteLog(logId),
    onSuccess: () => {
      invalidateAll()
      setDeletingLogId(null)
      toast.success(t('activity.deleteSuccess', 'Activity entry deleted'))
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('common.error', 'Error'))
    },
  })

  const createMutation = useMutation({
    mutationFn: (data) => dailyActivityAPI.createLog(data),
    onSuccess: () => {
      invalidateAll()
      setShowAddForm(false)
      toast.success(t('activity.createSuccess', 'Activity entry created'))
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('common.error', 'Error'))
    },
  })

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body animate-pulse">
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  const weeks = multiWeekData?.weeks || []
  if (weeks.length === 0 && !showAddForm) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">{t('activity.weeklyHistory', 'Weekly History')}</h4>
          <button onClick={() => setShowAddForm(true)} className="btn-primary text-sm flex items-center">
            <PlusIcon className="h-4 w-4 mr-1" />
            {t('activity.addEntry', 'Add Entry')}
          </button>
        </div>
        {showAddForm && (
          <ActivityEntryForm
            onSave={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddForm(false)}
            isPending={createMutation.isPending}
            t={t}
          />
        )}
      </div>
    )
  }

  const formatWeekLabel = (start, end) => {
    const s = new Date(start + 'T00:00:00')
    const e = new Date(end + 'T00:00:00')
    return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">{t('activity.weeklyHistory', 'Weekly History')}</h4>
        <button onClick={() => setShowAddForm(!showAddForm)} className="btn-primary text-sm flex items-center">
          <PlusIcon className="h-4 w-4 mr-1" />
          {t('activity.addEntry', 'Add Entry')}
        </button>
      </div>

      {/* VH-54: Add new entry form */}
      {showAddForm && (
        <ActivityEntryForm
          onSave={(data) => createMutation.mutate(data)}
          onCancel={() => setShowAddForm(false)}
          isPending={createMutation.isPending}
          t={t}
        />
      )}

      {weeks.map((week, idx) => {
        const isExpanded = expandedWeek === idx
        const hasData = week.days_with_data > 0

        return (
          <div key={idx} className="card overflow-hidden">
            <div
              className="card-body cursor-pointer hover:bg-gray-50"
              onClick={() => setExpandedWeek(isExpanded ? null : idx)}
            >
              {/* Summary row */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatWeekLabel(week.period_start, week.period_end)}
                  </p>
                  {!hasData && (
                    <p className="text-xs text-gray-400">{t('activity.noDataThisWeek', 'No data this week')}</p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {hasData && (
                    <div className="flex gap-3 text-sm">
                      {week.avg_steps != null && (
                        <span className="text-blue-600 font-semibold">
                          {week.avg_steps.toLocaleString()} <span className="text-xs font-normal text-gray-400">{t('activity.stepsPerDay', 'steps/d')}</span>
                        </span>
                      )}
                      {week.avg_calories_burned != null && (
                        <span className="text-orange-600 font-semibold hidden md:inline">
                          {week.avg_calories_burned.toLocaleString()} <span className="text-xs font-normal text-gray-400">{t('activity.kcalPerDay', 'kcal/d')}</span>
                        </span>
                      )}
                    </div>
                  )}
                  {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded: weekly totals + daily logs */}
              {isExpanded && hasData && (
                <div className="mt-4 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                  {/* Week averages */}
                  <p className="text-xs font-medium text-gray-500 mb-2">{t('activity.weekAverages', 'Week Averages')}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {week.avg_steps != null && (
                      <div className="bg-blue-50 rounded-lg p-2 text-center">
                        <BoltIcon className="h-4 w-4 text-blue-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-blue-600">{week.avg_steps.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{t('activity.avgSteps')}</p>
                      </div>
                    )}
                    {week.avg_calories_burned != null && (
                      <div className="bg-orange-50 rounded-lg p-2 text-center">
                        <FireIcon className="h-4 w-4 text-orange-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-orange-600">{week.avg_calories_burned.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{t('activity.avgCaloriesBurned')}</p>
                      </div>
                    )}
                    {week.total_intensity_minutes != null && (
                      <div className="bg-green-50 rounded-lg p-2 text-center">
                        <ClockIcon className="h-4 w-4 text-green-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-green-600">{week.total_intensity_minutes}</p>
                        <p className="text-xs text-gray-500">{t('activity.totalIntensity')}</p>
                      </div>
                    )}
                  </div>

                  {/* Week totals */}
                  <p className="text-xs font-medium text-gray-500 mb-2">{t('activity.weekTotals', 'Week Totals')}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {week.total_steps != null && (
                      <div className="text-center">
                        <p className="text-sm font-semibold text-blue-600">{week.total_steps.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">{t('activity.totalSteps', 'Total steps')}</p>
                      </div>
                    )}
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-700">{week.days_with_data}</p>
                      <p className="text-xs text-gray-400">{t('activity.daysWithData')}</p>
                    </div>
                    {week.avg_resting_heart_rate != null && (
                      <div className="text-center">
                        <p className="text-sm font-semibold text-red-600">{week.avg_resting_heart_rate} {t('activity.bpm')}</p>
                        <p className="text-xs text-gray-400">{t('activity.avgRestingHR', 'Avg resting HR')}</p>
                      </div>
                    )}
                  </div>

                  {/* Daily logs with edit/delete (VH-54) */}
                  {week.daily_logs?.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-gray-500 mb-2">{t('activity.dailyBreakdown', 'Daily Breakdown')}</p>
                      <div className="space-y-2">
                        {week.daily_logs.map((log) => (
                          <div key={log.id}>
                            {editingLogId === log.id ? (
                              <ActivityEntryForm
                                initialData={log}
                                onSave={(data) => updateMutation.mutate({ logId: log.id, data })}
                                onCancel={() => setEditingLogId(null)}
                                isPending={updateMutation.isPending}
                                t={t}
                              />
                            ) : deletingLogId === log.id ? (
                              <DeleteConfirmDialog
                                onConfirm={() => deleteMutation.mutate(log.id)}
                                onCancel={() => setDeletingLogId(null)}
                                isPending={deleteMutation.isPending}
                                t={t}
                              />
                            ) : (
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                                <span className="font-medium text-gray-700 w-20">
                                  {new Date(log.log_date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
                                </span>
                                <div className="flex gap-3 text-xs flex-wrap justify-end flex-1">
                                  {log.steps != null && (
                                    <span className="text-blue-600">{log.steps.toLocaleString()} {t('activity.stepsShort', 'steps')}</span>
                                  )}
                                  {log.calories_burned != null && (
                                    <span className="text-orange-600">{log.calories_burned} {t('activity.kcal')}</span>
                                  )}
                                  {log.resting_heart_rate != null && (
                                    <span className="text-red-600">{log.resting_heart_rate} {t('activity.bpm')}</span>
                                  )}
                                  {log.intensity_minutes != null && (
                                    <span className="text-green-600">{log.intensity_minutes} {t('activity.intensityMin', 'int.min')}</span>
                                  )}
                                </div>
                                {/* VH-54: Edit/Delete buttons */}
                                <div className="flex gap-1 ml-2">
                                  <button
                                    onClick={() => setEditingLogId(log.id)}
                                    className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                                    title={t('common.edit', 'Edit')}
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeletingLogId(log.id)}
                                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                                    title={t('common.delete', 'Delete')}
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      })}

      {/* Load more */}
      {weeksToLoad < 12 && (
        <button
          onClick={() => setWeeksToLoad((w) => Math.min(w + 4, 12))}
          className="w-full text-sm text-indigo-600 hover:text-indigo-800 py-2"
        >
          {t('activity.loadMore', 'Load more weeks')}
        </button>
      )}
    </div>
  )
}

// --- Main ActivityTab export ---

export default function ActivityTab() {
  return (
    <div className="space-y-6">
      <DailyActivityCard />
      <CalorieBalanceBar />
      <MultiWeekActivityHistory />
    </div>
  )
}
